import React, { useState } from "react";
import { Box, FormControl, Grid, IconButton, InputAdornment, Link, Typography } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link as RouterLink } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import { getPasswordResetCompleteSchema, type PasswordResetCompleteData } from "../../../schemas";
import {
	ApplicationWordArt,
	AuthenticationButton,
	PasswordRules,
	StyledFormLabel,
	StyledTextField,
} from "../../../components";

export interface PasswordResetCompleteFormProps {
	readonly onSubmit: (password: string) => void;
	readonly isSubmitting: boolean;
	readonly errorMessage: string | null;
}

export const PasswordResetCompleteForm: React.FC<PasswordResetCompleteFormProps> = ({
	onSubmit,
	isSubmitting,
	errorMessage,
}) => {
	const intl = useIntl();
	const [showPassword, setShowPassword] = useState(false);

	const {
		handleSubmit,
		control,
		formState: { errors },
	} = useForm<PasswordResetCompleteData>({
		resolver: zodResolver(getPasswordResetCompleteSchema(intl)),
		defaultValues: {
			password: "",
		},
	});

	const handleFormSubmit = (data: PasswordResetCompleteData) => {
		onSubmit(data.password);
	};

	const togglePasswordVisibility = () => {
		setShowPassword(!showPassword);
	};

	return (
		<Grid container component="form" onSubmit={handleSubmit(handleFormSubmit)} direction="column" gap={6}>
			<Box sx={{ margin: "auto" }}>
				<ApplicationWordArt width={300} />
			</Box>

			<Grid container direction="column" alignItems="center">
				<Typography variant="h1" color="white">
					<FormattedMessage id="password-reset-complete.title" defaultMessage="Set new password" />
				</Typography>
				<Typography color="white">
					<FormattedMessage id="password-reset-complete.subtitle" defaultMessage="Enter your new password below" />
				</Typography>
			</Grid>

			<Grid container direction="column" gap={2}>
				<Controller
					name="password"
					control={control}
					render={({ field }) => (
						<>
							<FormControl error={Boolean(errorMessage) || Boolean(errors.password)}>
								<StyledFormLabel color="white">
									<FormattedMessage id="password-reset-complete.password" defaultMessage="New Password" />
								</StyledFormLabel>
								<StyledTextField
									placeholder={intl.formatMessage({
										id: "password-reset-complete.password.placeholder",
										defaultMessage: "Enter User Password",
									})}
									type={showPassword ? "text" : "password"}
									fullWidth
									autoFocus
									error={Boolean(errorMessage) || Boolean(errors.password)}
									helperText={errors.password?.message}
									{...field}
									slotProps={{
										formHelperText: {
											"data-testid": "password-reset-complete__text__password-error",
										},
										htmlInput: {
											"data-testid": "password-reset-complete__input__password",
										},
										input: {
											endAdornment: (
												<InputAdornment position="end">
													<IconButton
														aria-label={intl.formatMessage({
															id: "password-reset-complete.toggle-visibility",
															defaultMessage: "Toggle password visibility",
														})}
														onClick={togglePasswordVisibility}
														edge="end"
													>
														{showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
													</IconButton>
												</InputAdornment>
											),
										},
									}}
								/>
							</FormControl>

							{Boolean(errorMessage) && (
								<Typography color="error" data-testid="password-reset-complete__text__error">
									{errorMessage}
								</Typography>
							)}

							<PasswordRules password={field.value} />
						</>
					)}
				/>

				<Grid container gap={1} direction="column">
					<AuthenticationButton
						data-testid="password-reset-complete__button__submit"
						variant="contained"
						type="submit"
						fullWidth
						size="large"
						loading={isSubmitting}
					>
						<FormattedMessage id="password-reset-complete.submit" defaultMessage="Reset Password" />
					</AuthenticationButton>

					<Link
						to="/login"
						component={RouterLink}
						sx={{ alignSelf: "flex-end", color: "white" }}
						data-testid="password-reset-complete__link__back"
					>
						<FormattedMessage id="password-reset-complete.back" defaultMessage="Back to login" />
					</Link>
				</Grid>
			</Grid>
		</Grid>
	);
};
